import { flattenDeep, indexOf, isNull } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { formatDate } from '../../../../utils/luxon';
import { getMLReadyAgreementTypeIds } from '../../../admin/documents/store/selectors';
import { BaseEntity } from '../../../admin/entity/store/types';
import { MyRiskTolerance, RiskTolerance } from '../../../admin/risk-tolerance/store/types';
import { DatasetDefinitionDB, HiddenFields } from '../../store/types';
import { InstanceMLData } from '../../store/mlTypes';
import { isGroupInstanceField } from './typeAssertions';
import { CurrentClientRiskConversation, DatasetInstance, DatasetInstanceTimeline, DocumentDatasetRiskTolerance, DocumentFieldRiskOverride, DocumentMeta, DocumentToDelete, FormDatasetInstance, Hierarchy, Instance, ModalInstanceParentDetails, OpenFieldSection, SearchFieldSection, TimelineInstance, WizardFieldOpen } from './types';
import { getDatasetInstanceRoot } from '../../../../store/rootSelector';

export const getDatasetDefinition = (state: ApplicationState): DatasetDefinitionDB | null => getDatasetInstanceRoot(state).datasetDefinition;

export const getIsPublishingDatasetDefinition = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isPublishingDatasetDefinition;

export const getIsCheckingIfPublishing = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isCheckingForIfPublishing;

export const getSavedInstances = (state: ApplicationState): Instance[] => getDatasetInstanceRoot(state).savedInstances;

export const getCurrentInstances = (state: ApplicationState): Instance[] => getDatasetInstanceRoot(state).currentInstances;

export const getECSDatasetId = (state: ApplicationState): number | null => getDatasetInstanceRoot(state).ecsDatasetId;

export const getParentDatasetId = (state: ApplicationState): number | null => getDatasetInstanceRoot(state).parentDatasetId;

export const getParentExecutedDate = (state: ApplicationState): string | undefined => {
    const parentId = getDatasetInstanceRoot(state).parentDatasetId;
    if (parentId) {
        const parentInstance = getCurrentInstances(state).find(({ instance }) => instance.datasetId === parentId);
        if (parentInstance) {
            return formatDate(parentInstance.instance.executedDate);
        }
    }
    return undefined;
};

export const getDatasetInstance = (state: ApplicationState): Instance | null => {
    const parentDatasetId = getParentDatasetId(state);
    if (!parentDatasetId) {
        return null;
    }
    const savedInstances = getSavedInstances(state);
    return savedInstances.find(({ instance: { datasetId } }) => datasetId === parentDatasetId) || null;
};

export const getDatasetInstanceTimeline = (state: ApplicationState): DatasetInstanceTimeline | null => getDatasetInstanceRoot(state).timeline;

export const getFollowingTimelineInstance = (state: ApplicationState): TimelineInstance | null => {
    const datasetInstance = getDatasetInstance(state);
    const timeline = getDatasetInstanceTimeline(state);
    if (isNull(timeline) || isNull(datasetInstance)) {
        return null;
    }
    const selectedTimelineInstance = timeline.instances.find(({ datasetInstanceId }) => datasetInstance.instance.datasetInstanceId === datasetInstanceId);
    const indexOfSelected = indexOf(selectedTimelineInstance, timeline.instances);
    if (indexOfSelected < 0) {
        return null;
    }
    return timeline.instances[indexOfSelected + 1] || null;
};

export const getConversationMaxDate = (state: ApplicationState): string | null => getFollowingTimelineInstance(state)?.executedDate || null;

export const getDatasetInstanceHierarchy = (state: ApplicationState): Hierarchy[] => getDatasetInstanceRoot(state).hierarchy;

export const getOpenFieldsAndSections = (state: ApplicationState): OpenFieldSection[] => getDatasetInstanceRoot(state).openFieldsAndSections;

export const getOriginalDocumentId = (state: ApplicationState): number | null => {
    const datasetInstance = getDatasetInstance(state);
    if (!datasetInstance) {
        return null;
    }
    return datasetInstance.instance.originalDocumentId || null;
};

export const getInstanceDocumentId = (state: ApplicationState): number | null => {
    const datasetInstance = getDatasetInstance(state);
    if (!datasetInstance) {
        return null;
    }
    return datasetInstance.instance.documentId;
};

export const getIsEditing = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isEditing;

export const getIsUpdating = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isUpdating;

export const getIsSaving = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isSaving;

export const getIsLoading = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isLoading;

export const getIsOpeningDocumentAndInstance = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isOpeningDocumentAndInstance;

export const getInstanceUpdated = (state: ApplicationState): boolean => getDatasetInstanceHierarchy(state).some(({ hasUpdated }) => hasUpdated);

export const getUpdatedInstanceModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).updatedInstanceModalOpen;

export const getConfirmSaveModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).confirmSaveModalOpen;

export const getLinkedDocumentModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).linkedDocumentModalOpen;

export const getAnnexDefinitionFieldIds = (state: ApplicationState): string[] => getDatasetInstanceRoot(state).annexFieldIds;

export const getWizardFieldOpen = (state: ApplicationState): WizardFieldOpen | null => getDatasetInstanceRoot(state).wizardFieldOpen;

export const getSelectedLinkedEntities = (state: ApplicationState): string[] => {
    const currentInstances = getCurrentInstances(state);
    const ecsDatasetId = getECSDatasetId(state);
    if (isNull(ecsDatasetId)) {
        return [];
    }
    const ecsFormInstance = currentInstances.find(({ instance }) => !isNull(instance.datasetId) && instance.datasetId === ecsDatasetId)!.instance as FormDatasetInstance;
    const entities = flattenDeep(Object.values(ecsFormInstance.datasetFields)[0].map(field => !isGroupInstanceField(field) && field.settings.linkedEntities || []));
    return entities;
};

export const getAgencyLinkedEntities = (state: ApplicationState): BaseEntity[] => getDatasetInstanceRoot(state).agencyLinkedEntities;

export const getHiddenFields = (state: ApplicationState): HiddenFields => getDatasetInstanceRoot(state).hiddenFields;

export const getRiskToleranceForDefinitions = (state: ApplicationState): (RiskTolerance | MyRiskTolerance)[] => getDatasetInstanceRoot(state).riskToleranceForDefinitions;

export const getDocumentRiskFields = (state: ApplicationState): DocumentDatasetRiskTolerance[] => getDatasetInstanceRoot(state).documentRiskFields;

export const getDocumentRiskFieldsForDatasetId = (datasetId: number) => (state: ApplicationState): DocumentDatasetRiskTolerance | undefined => {
    const documentRiskFields = getDocumentRiskFields(state);
    return documentRiskFields.find(documentDatasetRiskFields => documentDatasetRiskFields.datasetId === datasetId);
};

export const getShowLegacy = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).showLegacy;

export const getMLData = (state: ApplicationState): InstanceMLData | null => getDatasetInstanceRoot(state).mlData;

export const getInstanceContainsMLData = (state: ApplicationState): boolean => !isNull(getMLData(state));

export const getMLDataUnconfirmed = (state: ApplicationState): boolean => {
    const mlData = getMLData(state);
    const agreementTypeIds = getMLReadyAgreementTypeIds(state);
    return !isNull(mlData) && (mlData.versionConfirmed < mlData.version) && (agreementTypeIds.includes(mlData.agreementTypeId));
};

export const getMLDataModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).mlDataModalOpen;

export const getAnnexConfigurationModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).annexConfigurationModalOpen;

export const getIsUpdatingInstanceAnnexDefinitions = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isUpdatingInstanceAnnexDefinitions;

export const getAllSectionsAndFields = (state: ApplicationState): SearchFieldSection[] => getDatasetInstanceRoot(state).allSearchFieldSections;

export const getFieldSectionSearchValue = (state: ApplicationState): string => getDatasetInstanceRoot(state).fieldSectionSearch;

export const getSearchFuzzyMatches = (state: ApplicationState): SearchFieldSection[] => getDatasetInstanceRoot(state).searchFuzzyMatches;

export const getSearchFieldSection = (state: ApplicationState): SearchFieldSection | null => getDatasetInstanceRoot(state).searchFieldSection;

export const getAmendmentMLDataModalOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).amendmentMLDataModalOpen;

export const getIsQueryingDocumentWithAI = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isQueryingDocumentWithAI;

export const getInstanceEcsStartPage = (state: ApplicationState): number | null => getDatasetInstanceRoot(state).instanceEcsStartPage;

export const getInstanceEcsEndPage = (state: ApplicationState): number | null => getDatasetInstanceRoot(state).instanceEcsEndPage;

export const getIsUpdatingECSPageInfo = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isUpdatingECSPageInfo;

export const getFormInstanceIsScrolling = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isScrollingFormInstance;

export const getSelectedDocumentRiskOverride = (state: ApplicationState): DocumentFieldRiskOverride | null => getDatasetInstanceRoot(state).selectedFieldRiskOverride;

export const getDocumentRiskOverrideModalOpen = (state: ApplicationState): boolean => !isNull(getSelectedDocumentRiskOverride(state));

export const getClientRiskConversation = (state: ApplicationState): CurrentClientRiskConversation => getDatasetInstanceRoot(state).currentClientRiskConversation;

export const getIsAutoUpdatingNettingAnalysis = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isAutoUpdatingNettingAnalysis;

// Modal Instance
export const getCurrentModalInstance = (state: ApplicationState): DatasetInstance | null => getDatasetInstanceRoot(state).currentModalInstance;

export const getSavedModalInstance = (state: ApplicationState): DatasetInstance | null => getDatasetInstanceRoot(state).savedModalInstance;

export const getModalInstanceOpen = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).modalInstanceOpen;

export const getModalInstanceLoading = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).modalInstanceLoading;

export const getModalInstanceSaving = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).modalInstanceSaving;

export const getModalInstanceUpdated = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).modalInstanceUpdated;

export const getModalInstanceParentDetails = (state: ApplicationState): ModalInstanceParentDetails | null => getDatasetInstanceRoot(state).modalInstanceParentDetails;

export const getSecondaryDocumentToDelete = (state: ApplicationState): DocumentToDelete | null => getDatasetInstanceRoot(state).secondaryDocumentToDelete;

export const getDeleteSecondaryDocumentModalOpen = (state: ApplicationState): boolean => !isNull(getSecondaryDocumentToDelete(state));

export const getEditDocumentMeta = (state: ApplicationState): DocumentMeta | null => getDatasetInstanceRoot(state).editMetaDocument;

export const getDocumentMetaHasUpdated = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).documentMetaHasUpdated;

export const getIsUpdatingMetaDetails = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isUpdatingMetaDetails;

export const getIsDeleting = (state: ApplicationState): boolean => getDatasetInstanceRoot(state).isDeleting;
